import { useState } from 'react';
import { T, Var } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { SystemEntity } from '@erp/shared-types';
import type { TaskResponse } from '@erp/rpc';
import { ArrowsClockwiseIcon, SignpostIcon } from '@phosphor-icons/react';
import useGetInboundEmail from '@/api/core/inbound-emails/getInboundEmail';
import { useReprocessInboundEmail } from '@/api/communications/useReprocessInboundEmail';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ProposeMemoryActions,
  ProposeMemoryForm,
} from '@/components/harness/ProposeMemoryForm';
import { documentScopeFromClassification } from '@/components/harness/proposeMemoryDraft';
import { useCan } from '@/hooks/useCan';
import { readSourceEntity } from './setTaskTypeForm';

/** The inbound email a routing task was raised for, if it was. */
function routedEmailId(task: TaskResponse): string | null {
  if (task.anchorEntityType === SystemEntity.INBOUND_EMAIL)
    return task.anchorEntityId;
  const source = readSourceEntity(task.metadata);
  return source?.entityType === SystemEntity.INBOUND_EMAIL
    ? source.entityId
    : null;
}

/**
 * "Teach Routing" on a task raised because an email could not be matched to
 * an organization: records a sender → organization memory (active at once,
 * the reviewer is the approver) and offers to reprocess the email so the
 * routing workflow re-runs with that memory. Only the task knows about
 * routing; the inbound-email screens stay generic.
 */
export function TeachRoutingButton({ task }: { task: TaskResponse }) {
  const inboundEmailId = routedEmailId(task);
  const [canApprove] = useCan(Action.Approve, Subject.MemoryProposal);
  const [open, setOpen] = useState(false);
  if (!inboundEmailId || !canApprove) return null;
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
      >
        <SignpostIcon className="size-4" />
        <T context="button on a routing task that opens the teach-routing dialog">
          Teach Routing
        </T>
      </Button>
      {open && (
        <TeachRoutingDialog
          inboundEmailId={inboundEmailId}
          open
          onOpenChange={setOpen}
        />
      )}
    </>
  );
}

function TeachRoutingDialog({
  inboundEmailId,
  open,
  onOpenChange,
}: {
  inboundEmailId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: email, isLoading } = useGetInboundEmail({ inboundEmailId });
  const reprocess = useReprocessInboundEmail();
  const [saved, setSaved] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title: teach where a sender's documents route">
              Teach Routing
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="dialog description: sender route memory is active at once and used on reprocess">
              The route is saved as an active memory in this organization. The
              classifier applies it the next time this sender's emails are
              processed, including a reprocess of this one.
            </T>
          </DialogDescription>
        </DialogHeader>
        {isLoading || !email ? (
          <p className="text-sm text-muted-foreground">
            <T context="loading the email behind a routing task">
              Loading email…
            </T>
          </p>
        ) : saved ? (
          <div className="flex flex-col gap-3 text-sm">
            <p>
              <T context="after a sender route was saved: offer to reprocess the email">
                Memory saved. Reprocess <Var>{email.subject}</Var> to route it
                now, then close this task once a group or draft exists.
              </T>
            </p>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                <T context="close the dialog">Done</T>
              </Button>
              <Button
                disabled={reprocess.isPending}
                onClick={() =>
                  reprocess.mutate(
                    { inboundEmailId },
                    {
                      onSuccess: () => onOpenChange(false),
                    },
                  )
                }
              >
                <ArrowsClockwiseIcon className="size-4" />
                <T context="button: run the email through processing again">
                  Reprocess Email
                </T>
              </Button>
            </div>
          </div>
        ) : (
          <ProposeMemoryForm
            initialMode="sender_route"
            lockMode
            initialSender={email.from}
            initialDocumentScope={documentScopeFromClassification(
              email.classification?.classificationResult,
            )}
            acceptImmediately
            onSubmitted={() => setSaved(true)}
            footer={(state) => (
              <ProposeMemoryActions
                {...state}
                activates
                secondary={
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    <T context="cancel button">Cancel</T>
                  </Button>
                }
              />
            )}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
